/**
 * One place where every request to the analysis backend is issued.
 *
 * The client makes a lot of small requests — a manifest, peak blocks,
 * spectrogram tiles, and PCM blocks, hundreds of them over a session — from four
 * different modules. Authentication has to reach all of them or none of them are
 * usable, so they all come through here.
 *
 * `fetchOptions` may be a plain `RequestInit`, or a function returning one.
 * **The function form is the one that matters for real deployments**: a bearer
 * token captured once at construction expires mid-session, and the symptom is a
 * track that loads and then quietly stops refining as every later block 401s.
 * Resolving per request means the caller can hand back a fresh token, and the
 * function may be async so it can await a refresh.
 */

/**
 * @param {RequestInit|Function|null} [fetchOptions]
 * @returns {Promise<RequestInit|undefined>}
 */
export async function resolveFetchOptions(fetchOptions) {
  if (!fetchOptions) return undefined;
  const resolved = typeof fetchOptions === "function" ? await fetchOptions() : fetchOptions;
  return resolved || undefined;
}

/**
 * `fetch`, with the host's request options applied.
 *
 * @param {string} url
 * @param {RequestInit|Function|null} [fetchOptions]
 * @returns {Promise<Response>}
 */
export async function remoteFetch(url, fetchOptions) {
  const init = await resolveFetchOptions(fetchOptions);
  return init ? fetch(url, init) : fetch(url);
}
